import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "../brand/Logo";

const NAV_LINKS = [
    { to: "/technology", label: "Technology" },
    { to: "/industries", label: "Industries" },
    { to: "/about", label: "About" },
];

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-[#050B14]/85 backdrop-blur-xl border-b border-white/10"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
                <Link to="/" data-testid="nav-home-link" className="shrink-0">
                    <Logo variant="dark" />
                </Link>

                <nav className="hidden lg:flex items-center gap-1">
                    {NAV_LINKS.map((link) => (
                        <NavLink
                            key={link.to}
                            to={link.to}
                            data-testid={`nav-${link.label.toLowerCase()}-link`}
                            className={({ isActive }) =>
                                `relative px-4 py-2 text-sm font-medium tracking-wide transition-colors ${
                                    isActive
                                        ? "text-[#00E5FF]"
                                        : "text-white/70 hover:text-white"
                                }`
                            }
                        >
                            {link.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <Link
                        to="/contact"
                        data-testid="nav-contact-cta"
                        className="hidden lg:inline-flex items-center gap-1.5 bg-[#00E5FF] text-[#050B14] px-4 py-2 rounded-md text-sm font-semibold hover:bg-white transition-colors"
                    >
                        Discuss Your Project
                        <ArrowUpRight className="w-4 h-4" />
                    </Link>
                    <button
                        type="button"
                        onClick={() => setOpen((v) => !v)}
                        data-testid="nav-mobile-toggle"
                        className="lg:hidden w-10 h-10 inline-flex items-center justify-center rounded-md border border-white/15 text-white"
                        aria-label="Toggle menu"
                    >
                        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden bg-[#050B14]/95 backdrop-blur-xl border-t border-white/10">
                    <nav className="px-6 py-6 flex flex-col gap-1">
                        {NAV_LINKS.map((link) => (
                            <NavLink
                                key={link.to}
                                to={link.to}
                                data-testid={`nav-mobile-${link.label.toLowerCase()}-link`}
                                className={({ isActive }) =>
                                    `px-3 py-3 text-base font-medium border-b border-white/5 ${
                                        isActive ? "text-[#00E5FF]" : "text-white/80"
                                    }`
                                }
                            >
                                {link.label}
                            </NavLink>
                        ))}
                        <Link
                            to="/contact"
                            data-testid="nav-mobile-contact-cta"
                            className="mt-4 inline-flex items-center justify-center gap-1.5 bg-[#00E5FF] text-[#050B14] px-4 py-3 rounded-md text-sm font-semibold"
                        >
                            Discuss Your Project
                            <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </nav>
                </div>
            )}
        </header>
    );
};

export default Navbar;
